// Vendor Returns list — filters, pagination, drill-in.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorToMessage } from "@/lib/apiClient";

const STATUSES = ["DRAFT", "SENT", "RECEIVED_BACK", "CLOSED", "CANCELLED"];

const statusChip = {
  DRAFT:         "bg-slate-100 text-slate-700 border-slate-200",
  SENT:          "bg-amber-100 text-amber-800 border-amber-200",
  RECEIVED_BACK: "bg-sky-100   text-sky-800   border-sky-200",
  CLOSED:        "bg-emerald-100 text-emerald-800 border-emerald-200",
  CANCELLED:     "bg-rose-100  text-rose-800  border-rose-200",
};

export default function VendorReturnsPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = { page, page_size: 25 };
      if (statusFilter) params.status_filter = statusFilter;
      const { data } = await api.get("/vendor-returns", { params });
      let items = data.items || [];
      if (q.trim()) {
        const needle = q.trim().toLowerCase();
        items = items.filter((r) =>
          (r.return_no || "").toLowerCase().includes(needle) ||
          (r.vendor_name || "").toLowerCase().includes(needle));
      }
      setRows(items); setTotal(data.total || 0);
    } catch (e) { setError(errorToMessage(e, "Could not load Vendor Returns")); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, statusFilter]);

  return (
    <section className="p-6" data-testid="vr-list-page">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Vendor Returns</h1>
          <p className="text-sm text-slate-500">Universal for every Passport-enabled item.</p>
        </div>
        <button
          onClick={() => nav("/admin/vendor-returns/new")}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          data-testid="vr-new-btn">
          + New Vendor Return
        </button>
      </header>

      <div className="mb-3 flex items-center gap-2">
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search VR number or vendor name"
          className="w-64 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          data-testid="vr-search"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          data-testid="vr-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">
          Refresh
        </button>
        {loading && <span className="text-xs text-slate-500">Loading…</span>}
      </div>

      {error && <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">VR No</th>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-right">Items</th>
              <th className="px-3 py-2 text-right">Open</th>
              <th className="px-3 py-2 text-right">Closed</th>
              <th className="px-3 py-2 text-left">Courier</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-slate-500">
                No Vendor Returns yet.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}
                  className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                  onClick={() => nav(`/admin/vendor-returns/${r.id}`)}
                  data-testid={`vr-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-blue-700 font-semibold">{r.return_no}</td>
                <td className="px-3 py-2">{r.vendor_name}</td>
                <td className="px-3 py-2 text-slate-600">{r.return_date}</td>
                <td className="px-3 py-2 text-right">{r.total_items}</td>
                <td className="px-3 py-2 text-right">{r.open_items}</td>
                <td className="px-3 py-2 text-right">{r.closed_items}</td>
                <td className="px-3 py-2 text-slate-600">{r.courier_name || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusChip[r.status] || ""}`}>
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
        <div>Total: {total}</div>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40">Prev</button>
          <span>Page {page}</span>
          <button disabled={rows.length < 25} onClick={() => setPage((p) => p + 1)}
                  className="rounded border border-slate-200 px-2 py-1 disabled:opacity-40">Next</button>
        </div>
      </div>
    </section>
  );
}
